// RoleBasedRoute.js
import React, { useState, useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import Dashboard from './Dashboard';
import RegionalDashboard from './RegionalDashboard';
import CorporateDashboard from './CorporateDashboard'; // New dashboard for corporate access
import { getFirstAvailableContract, mapContractToRoute } from './utils/contractUtils';
import API_BASE_URL from './config/api';

function RoleBasedRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasCheckedInitialRedirect, setHasCheckedInitialRedirect] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const token = localStorage.getItem('managerToken');
  const userStr = localStorage.getItem('managerUser');
  
  // Memoize user parsing to prevent unnecessary re-renders
  const { user, buildingId } = useMemo(() => {
    let user = null;
    let buildingId = "";
    
    try {
      user = userStr ? JSON.parse(userStr) : null;
      buildingId = user?.buildingId || "";
    } catch (err) {
      console.error("Error parsing managerUser:", err);
    }
    
    return { user, buildingId };
  }, [userStr]);

  // For managers, check if we need to redirect to first available contract
  useEffect(() => {
    const checkAndRedirect = async () => {
      // Don't run if no token/user, or if we've already checked
      if (!token || !user || hasCheckedInitialRedirect || !buildingId) {
        setIsLoading(false);
        return;
      }

      // Check if we've already done the initial redirect in this session
      const hasDoneInitialRedirect = localStorage.getItem('hasDoneInitialRedirect') === 'true';
      if (hasDoneInitialRedirect) {
        setHasCheckedInitialRedirect(true);
        setIsLoading(false);
        return;
      }

      try {
        const resp = await fetch(
          `${API_BASE_URL}/api/getBuildingById`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ buildingId })
          }
        );
        
        if (!resp.ok) {
          throw new Error(`Error fetching building data: ${resp.status}`);
        }
        
        const data = await resp.json();
        const contracts = data.contracts || [];
        
        // Only redirect if we're on the root path and have contracts
        if (location.pathname === '/' && contracts.length > 0) {
          const firstContract = getFirstAvailableContract(contracts);
          if (firstContract && firstContract !== 'hvac-maintenance') {
            const targetRoute = mapContractToRoute(firstContract);
            console.log(`Redirecting to first available contract: ${firstContract} -> ${targetRoute}`);
            // Mark that we've done the initial redirect
            localStorage.setItem('hasDoneInitialRedirect', 'true');
            setHasCheckedInitialRedirect(true);
            navigate(targetRoute, { replace: true });
            return;
          }
        }
        
        setHasCheckedInitialRedirect(true);
        setIsLoading(false);
      } catch (err) {
        console.error("Error checking contracts for redirect:", err);
        setHasCheckedInitialRedirect(true);
        setIsLoading(false);
      }
    };

    checkAndRedirect();
  }, [token, buildingId, hasCheckedInitialRedirect, location.pathname, navigate, user]);
  
  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }

  console.log("user role: ", user);

  // Maintenance-role users belong in the internal hub, not the building
  // dashboard. Send them there immediately UNLESS this is an explicit
  // preview request (`/?buildingId=X&preview=1`) coming from the Home
  // page's "View" button — in that case fall through to <Dashboard /> and
  // let Dashboard.js pick the building ID off the URL.
  {
    const roleLc = String(user.role || '').toLowerCase();
    if (roleLc === 'maintenance' || roleLc.startsWith('maintenance-')) {
      const params = new URLSearchParams(location.search);
      const isPreviewing = params.get('buildingId') && params.get('preview') === '1';
      if (!isPreviewing) {
        return <Navigate to="/maintenance" replace />;
      }
    }
  }

  // Check for corporate roles first: owner and developer.
  if (user.role === 'corporate') {
    return <CorporateDashboard />;
  }

  // If the user's role is "regional", render the RegionalDashboard.
  if (user.role === 'regional') {
    return <RegionalDashboard />;
  }

  // Show loading while checking contracts
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Otherwise, assume it's a manager and render the standard Dashboard.
  return <Dashboard />;
}

export default RoleBasedRoute;
